
import { SEO } from "@/components/SEO";
import { Hero } from "@/components/Hero";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Card, CardContent } from "@/components/ui/card";
import { ServiceContactForm } from "@/components/service/ServiceContactForm";

const GeneralContractorsPage = () => {
  const breadcrumbItems = [
    { label: "Services", href: "/services" },
    { label: "General Contractors", href: "/general-contractors-oklahoma-city" },
  ];

  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "General Contractors in Oklahoma City",
    "description": "Connect with established general contractors in Oklahoma City for residential and commercial construction projects.",
    "provider": {
      "@type": "Organization",
      "name": "Home Pros OKC"
    },
    "areaServed": {
      "@type": "City",
      "name": "Oklahoma City"
    },
    "serviceType": "General Contractors"
  };

  return (
    <div className="min-h-screen">
      <SEO
        title="General Contractors in Oklahoma City | Home Pros OKC"
        description="Find reliable, experienced general contractors in Oklahoma City for your residential or commercial construction project. Get free quotes from top pros."
        canonicalUrl="/general-contractors-oklahoma-city"
        schema={schema}
      />

      <div className="container mx-auto px-4 py-8">
        <Breadcrumbs items={breadcrumbItems} />
        
        <Hero
          title="General Contractors in Oklahoma City"
          subtitle="Find experienced, professional general contractors for all your construction and remodeling needs"
          buttonText="Get Free Quotes"
          buttonLink="#contact-form"
        />

        <div className="my-12">
          <h2 className="text-2xl font-bold mb-6">Your Oklahoma City Construction Partners</h2>
          <p className="text-lg mb-8">
            Whether you're planning a home renovation, commercial build-out, or a complete custom home construction, 
            our network of trusted general contractors in Oklahoma City can help bring your vision to life.
          </p>
          
          <div className="grid md:grid-cols-3 gap-6">
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold mb-2">Comprehensive Project Management</h3>
                <p>Our general contractors handle every aspect of your construction project from permits and planning to the final walkthrough.</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold mb-2">Quality Craftsmanship</h3>
                <p>Work with experienced builders who take pride in delivering exceptional results that stand the test of time.</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold mb-2">Transparent Communication</h3>
                <p>Stay informed throughout your project with clear timelines, detailed estimates, and regular progress updates.</p>
              </CardContent>
            </Card>
          </div>
        </div>
        
        <div className="my-12">
          <h2 className="text-2xl font-bold mb-6">Services Our General Contractors Provide</h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold mb-2">Residential Construction</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Custom home building</li>
                  <li>Home additions and expansions</li>
                  <li>Kitchen and bathroom remodeling</li>
                  <li>Basement finishing</li>
                  <li>Deck and patio construction</li>
                  <li>Garage conversions and additions</li>
                </ul>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold mb-2">Commercial Construction</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Office build-outs and renovations</li>
                  <li>Retail space construction</li>
                  <li>Restaurant construction</li>
                  <li>Warehouse and industrial buildings</li>
                  <li>Medical facility construction</li>
                  <li>Multi-family housing development</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="my-12 p-6 bg-muted rounded-lg">
          <h2 className="text-2xl font-bold mb-4">Why Choose a Professional General Contractor?</h2>
          <p className="mb-4">
            Hiring a professional general contractor for your construction project offers numerous benefits:
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li>
              <span className="font-semibold">Expert Coordination:</span> Your general contractor manages all subcontractors, ensuring seamless collaboration between specialists.
            </li>
            <li>
              <span className="font-semibold">Code Compliance:</span> Stay current with building codes and regulations to ensure your project passes all required inspections.
            </li>
            <li>
              <span className="font-semibold">Time Savings:</span> Professional management means your project stays on schedule and avoids costly delays.
            </li>
            <li>
              <span className="font-semibold">Quality Assurance:</span> Experienced contractors recognize quality workmanship and ensure all aspects of the project meet high standards.
            </li>
            <li>
              <span className="font-semibold">Problem Resolution:</span> When challenges arise, your contractor has the experience to find efficient solutions quickly.
            </li>
          </ul>
        </div>

        <div className="my-12">
          <h2 className="text-2xl font-bold mb-6">Ready to Start Your Construction Project?</h2>
          <p className="text-lg mb-6">
            Connect with top-rated general contractors in Oklahoma City today. Fill out our simple form below to receive free, no-obligation quotes for your project.
          </p>
          
          <ServiceContactForm 
            title="Request a Free Quote"
            description="Tell us about your project, and we'll connect you with the best general contractors in Oklahoma City."
          />
        </div>

        <div className="mt-12 text-center">
          <h3 className="text-xl font-semibold mb-4">Looking for Our Top-Rated General Contractors?</h3>
          <p className="mb-6">
            View our curated list of the best general contractors in Oklahoma City.
          </p>
          <a 
            href="/best-general-contractors-oklahoma-city" 
            className="inline-block px-6 py-3 bg-primary text-white rounded-md hover:bg-primary/90 transition"
          >
            See Top-Rated Contractors
          </a>
        </div>
      </div>
    </div>
  );
};

export default GeneralContractorsPage;
